import React, { useEffect, useRef, useState, useContext } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import PerfectScrollbar from "perfect-scrollbar";

import AdminNavbar from "components/navbars/AdminNavbar.js";
import Footer from "components/footer/Footer.js";
import Sidebar from "components/sidebar/Sidebar.js";

import routes from "routes.js";
import logo from "assets/img/logo.png";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import { UserSettingsContext } from "contexts/UserSettingsContext";
import { useTranslation } from "react-i18next";

function Admin() {
    const { t } = useTranslation();
    const [routeKeys, setRouteKeys] = useState({});
    const location = useLocation();
    const navigate = useNavigate();
    const mainPanelRef = useRef(null);
    const [sidebarOpened, setSidebarOpened] = useState(document.documentElement.className.includes("nav-open"));
    const { color, changeColor } = useContext(BackgroundColorContext);
    const { tenant, userSettings, updateSetting } = useContext(UserSettingsContext);

    const handleSwitchApp = (newSelectedApp) => {
        updateSetting('selected_app', newSelectedApp);
        navigate(`/admin/dashboard/`, { replace: false })    
    }

    // Initialize PerfectScrollbar
    useEffect(() => {
    if (navigator.platform.includes("Win")) {
        document.documentElement.classList.add("perfect-scrollbar-on");
        document.documentElement.classList.remove("perfect-scrollbar-off");
        new PerfectScrollbar(mainPanelRef.current, { suppressScrollX: true });
        
        document.querySelectorAll(".table-responsive").forEach(table => {
        new PerfectScrollbar(table);
        });
    }

    return () => {
        if (navigator.platform.includes("Win")) {
            // Clean up the scrollbar
            PerfectScrollbar?.instances?.forEach(psInstance => psInstance.destroy());
            document.documentElement.classList.add("perfect-scrollbar-off");
            document.documentElement.classList.remove("perfect-scrollbar-on");
        }
    };
    }, []);

    // Scroll to top on route change
    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
        if (mainPanelRef.current) {
            mainPanelRef.current.scrollTop = 0;
        }    
    }, [location]);

    // Toggle the sidebar on small devices
    const toggleSidebar = () => {
        document.documentElement.classList.toggle("nav-open");
        setSidebarOpened(!sidebarOpened);
    };

    function getRoutes(routes) {
        return routes.map((prop, key) => {
            if (prop.layout === "/admin") {
                if (prop.children) {
                    return (
                    <Route key={key} path={prop.path} element={prop.component}>
                        {prop.children.map((child, childKey) => (
                            <Route
                                key={childKey}
                                path={child.path}
                                element={React.cloneElement(child.component, { key: routeKeys[child.path] || childKey })}
                            />
                        ))}
                    </Route>
                    );
                } else {
                    return (
                    <Route
                        path={prop.path}
                        element={React.cloneElement(prop.component, { key: routeKeys[prop.path] || key })}
                        key={key}
                    />
                    );
                }
            }
            return null;
        });
    }

    const getFilteredRoutes = (routes) => {
        const filteredRoutes = routes.filter(route => route[userSettings.selected_app]).filter(route => {
            return true || userSettings[route.key];
        });

        return filteredRoutes
    };

    const resetComponent = (path) => {
        // Update the key for the specific route to force a remount
        setRouteKeys(prev => ({ ...prev, [path]: Date.now() }));
    };

    // Get brand text based on the current pathname
    const getBrandText = () => {
        const route = routes.find(r => location.pathname.includes(r.layout + r.path));
        return route ? t(`menu.${route.name}`) : "Brand";
    };

    return (
        <div className="wrapper">
            <Sidebar
                routes={getFilteredRoutes(routes)}
                selectedApp={userSettings.selected_app}
                setSelectedApp={handleSwitchApp}
                toggleSidebar={toggleSidebar}
                onNavigate={resetComponent} 
            />
            <div className="main-panel" ref={mainPanelRef} data={color}>
                <AdminNavbar
                    brandText={getBrandText()}
                    toggleSidebar={toggleSidebar}
                    sidebarOpened={sidebarOpened}
                    logo={{text: tenant?.name || 'Mainition', logo_upload_id: tenant?.logo_upload_id, imgSrc: logo}}
                />
                <Routes>
                    {getRoutes(routes)}
                    <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
                </Routes>
                {location.pathname !== "/admin/maps" && <Footer />}
            </div>
        </div>);
    }

export default Admin;
